import {
  Authorizer,
  CookieAuthServiceAuthorizationProvider,
  OlympusAuthorizationProvider,
} from '@owner/auth'
import type { OlympusUser, OlympusBrandSession } from '@owner/auth'
import { shallowRef, type Ref } from 'vue'

import { logError } from '@/logger/logger'

import type { BrandSession, User } from './types'

const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL

let authorizerInstance: Authorizer | null = null

/**
 * Reactive handle on the Authorizer. Flips from `null` to the instance after
 * `initializeAuthorizer` resolves, and back to `null` on `resetAuthorizer`.
 * Read this in computeds (e.g. the sidebar nav filter) so they re-evaluate
 * once the Authorizer is ready, instead of caching a `null` result.
 */
const authorizerRef: Ref<Authorizer | null> = shallowRef(null)

export const useAuthorizerRef = (): Ref<Authorizer | null> => authorizerRef

/**
 * Get the Authorizer singleton instance
 * Components use this to access authorizer.can(), authorizer.hasRole(), etc. directly
 */
export const useAuthorizer = (): Authorizer => {
  if (!authorizerInstance) {
    throw new Error(
      'Authorizer not initialized. Call initializeAuthorizer() after login.'
    )
  }
  return authorizerInstance
}

const initializeAuthorizer = async (
  user: OlympusUser,
  brandSession: OlympusBrandSession,
  sessionProvider?: 'olympus' | 'auth-service',
  getCsrfToken?: () => string | null | undefined
): Promise<void> => {
  const provider =
    sessionProvider === 'auth-service'
      ? new CookieAuthServiceAuthorizationProvider({
          authServiceUrl: AUTH_SERVICE_URL,
          // Wildcard for internal users with no brand context; auth-service
          // gates wildcard permissions on isInternalUser server-side.
          brandId: brandSession.brandId ?? '*',
          // Read the CSRF token lazily on each request so the latest
          // value (set after login or refresh) is always used. Caller
          // owns the storage so this package stays decoupled from the
          // Hermes auth store.
          getCsrfToken: getCsrfToken ?? ((): null => null),
        })
      : new OlympusAuthorizationProvider(user, brandSession)

  authorizerInstance = new Authorizer({
    // No background refresh needed - Hermes re-initializes on login/brand switch
    autoRefresh: false,
    provider,
  })
  await authorizerInstance.initialize()
  authorizerRef.value = authorizerInstance
}

export const resetAuthorizer = (): void => {
  if (authorizerInstance) {
    authorizerInstance.destroy()
  }
  authorizerInstance = null
  authorizerRef.value = null
}

/**
 * Get the Authorizer instance if initialized, or null otherwise
 * Use this for conditional checks where you need TypeScript type narrowing
 */
export const getAuthorizer = (): Authorizer | null => {
  return authorizerInstance
}

/**
 * Safely initialize the Authorizer with feature flag and validation checks.
 * This function is idempotent - safe to call multiple times.
 * Errors are logged but don't block the calling flow.
 * @param user - The authenticated user
 * @param brandSession - The brand session to initialize the authorizer for
 * @param context - Context string for error logging
 * @param sessionProvider
 * @param getCsrfToken - Lazy reader for the CSRF token. Required when
 *   sessionProvider === 'auth-service' so the cookie provider can attach
 *   X-CSRFToken on each request.
 */
export const safeInitializeAuthorizer = async (
  user: User,
  brandSession: BrandSession,
  context: string,
  sessionProvider?: 'olympus' | 'auth-service',
  getCsrfToken?: () => string | null | undefined
): Promise<void> => {
  if (getAuthorizer()) {
    return
  }

  try {
    await initializeAuthorizer(
      user,
      brandSession,
      sessionProvider,
      getCsrfToken
    )
  } catch (error) {
    logError(error, { context: `${context} - Failed to initialize Authorizer` })
  }
}
